import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AdvancementStatus } from '@appTypes/enums';

import type { MBCDashboardSlice, ModalName } from '../types';
import { ViewByTabs } from './constants';
import { YouthByMBRowKey } from './utils';

const initialState: MBCDashboardSlice = {
  youthSearch: '',
  viewBy: ViewByTabs.MeritBadge,
  sortByName: 'lastName',
  filterByStatus: null,
  /** Member IDS of the selected youth */
  selectedYouth: [],
  selectedYouthByMB: [],
  selectedRequirements: [],
  selectedMeritBadges: [],
  modals: { removeYouth: false, toApprove: false, classWorksheet: false },
  navigatedToMb: false,
};

export const mbcDashboardSlice = createSlice({
  name: 'mbcDashboard',
  initialState,
  reducers: {
    setViewBy(state, action: PayloadAction<MBCDashboardSlice['viewBy']>) {
      state.viewBy = action.payload;
      // Reset any selection after changing view
      state.selectedYouthByMB = [];
      state.selectedYouth = [];
    },
    setSortByName(
      state,
      action: PayloadAction<MBCDashboardSlice['sortByName']>,
    ) {
      state.sortByName = action.payload;
    },
    setModal(
      state,
      action: PayloadAction<{ modal: ModalName; open: boolean }>,
    ) {
      state.modals[action.payload.modal] = action.payload.open;
    },
    setSelectedMeritBadges(
      state,
      action: PayloadAction<MBCDashboardSlice['selectedMeritBadges']>,
    ) {
      state.selectedMeritBadges = action.payload;
    },
    setSelectedRequirements(
      state,
      action: PayloadAction<MBCDashboardSlice['selectedRequirements']>,
    ) {
      state.selectedRequirements = action.payload;
    },
    setYouthSearch(state, action: PayloadAction<string>) {
      state.youthSearch = action.payload;
    },
    /** Items selected on the list of youths */
    setSelectedYouth(state, action: PayloadAction<number[]>) {
      state.selectedYouth = action.payload;
    },
    /** When navigating to MB, hide breadcrumbs */
    setNavigatedToMb(state, action: PayloadAction<boolean>) {
      state.navigatedToMb = action.payload;
    },
    setFilterByStatus(state, action: PayloadAction<AdvancementStatus | null>) {
      state.filterByStatus = action.payload;
    },
    /** Items selected on the view grouped by merit badge */
    setSelectedYouthByMB(
      state,
      action: PayloadAction<{
        selectedRowKeys: MBCDashboardSlice['selectedYouthByMB'];
        meritBadgeId: number;
      }>,
    ) {
      const newItems = state.selectedYouthByMB.filter(
        item =>
          YouthByMBRowKey.parseKey(item).meritBadgeId !==
          action.payload.meritBadgeId,
      );
      newItems.push(...action.payload.selectedRowKeys);
      state.selectedYouthByMB = newItems;
    },
    resetItem(state, action: PayloadAction<Array<keyof MBCDashboardSlice>>) {
      const { payload } = action;
      for (const key of payload) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state as any)[key] = initialState[key];
      }
    },
    toggleSelectedYouth(
      state,
      action: PayloadAction<{ memberId: number; checked: boolean }>,
    ) {
      if (action.payload.checked) {
        state.selectedYouth.push(action.payload.memberId);
      } else {
        const index = state.selectedYouth.findIndex(
          item => item === action.payload.memberId,
        );

        if (index > -1) {
          state.selectedYouth.splice(index, 1);
        }
      }
    },
  },
});

export const mbcDashboardActions = mbcDashboardSlice.actions;
